//#region IMPORTS
import {
  setSelectedInstoreOrders,
  setInstoreOrderPageNumber,
  toggleSelectedInstoreOrder,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { InstoreOrder } from "../../models/instoreOrder";
import { MetaData } from "../../models/pagination";
import InstoreOrdersTableHeader from "./InstoreOrdersTableHeader";
import InstoreOrdersTableRow from "./InstoreOrdersTableRow";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import useHQmanager from "../../hooks/useHQmanager";
import MainTable from "../_layout/MainTable";
import PaginationBarClient from "../paginationBar/PaginationBarClient";
//#endregion

//#region INTERFACE
interface Props {
  changeOrderStatusHandler: (
    instoreOrder: InstoreOrder,
    status: string
  ) => void;
  deleteHandler: (item: InstoreOrder) => void;
  enableSelection?: boolean;
  clientPaginate?: boolean;
  itemsLoaded: boolean;
  metaData?: MetaData;
  items: InstoreOrder[];
  hide?: boolean;
}
//#endregion

export default function InstoreOrdersTable({
  changeOrderStatusHandler,
  deleteHandler,
  enableSelection = false,
  clientPaginate = false,
  itemsLoaded,
  metaData,
  items,
  hide = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.account);
  const selected = useAppSelector(
    (state) => state.instoreOrders.selectedInstoreOrders,
  );

  // Roles
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);
  const isPicker = usePickerManager(user);
  const isShop = !isAdmin && !isHq && !isPicker;

  // HQ only needs the actions column on the Approve preset
  const { search } = useLocation();
  const isApprovePreset =
    new URLSearchParams(search).get("preset") === "approve";
  const showActions = !isShop && (!isHq || isApprovePreset);
  // The decline (approve/decline) toggle applies to admin + picker on pending
  // orders, i.e. the Approve preset. Hidden elsewhere (and for HQ) so the column
  // does not leave an empty gap before the actions.
  const showDecline = (isAdmin || isPicker) && isApprovePreset;

  const allSelected =
    items.length > 0 && items.every((o) => selected.includes(o.id));
  const someSelected = items.some((o) => selected.includes(o.id));
  const toggleAll = () => {
    if (allSelected) {
      dispatch(setSelectedInstoreOrders([]));
    } else {
      dispatch(setSelectedInstoreOrders(items.map((o) => o.id)));
    }
  };

  // Client-side pagination (e.g. Manage: all orders are fetched at once)
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const pagedItems = clientPaginate
    ? items.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
    : items;

  // Keep the current page in range when the list shrinks
  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [pageCount, pageIndex]);

  // Drop the selection whenever a server refetch is pending (preset / status /
  // limit change), so stale ids from a previous list never linger.
  useEffect(() => {
    if (!itemsLoaded) dispatch(setSelectedInstoreOrders([]));
  }, [itemsLoaded, dispatch]);
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      <MainTable
        itemsLoaded={itemsLoaded}
        clientPaginate={clientPaginate}
        metaData={metaData}
        pageType="orders"
        onPageChangeHandler={(page: number) =>
          dispatch(setInstoreOrderPageNumber({ page: page }))
        }
        header={
          <InstoreOrdersTableHeader
            isAdmin={isAdmin}
            isPicker={isPicker}
            isHQ={isHq}
            isShop={isShop}
            showActions={showActions}
            showDecline={showDecline}
            enableSelection={enableSelection}
            allSelected={allSelected}
            someSelected={someSelected && !allSelected}
            onToggleAll={toggleAll}
          />
        }
        hide={hide}
      >
        {pagedItems.map((instoreOrder) => (
          <InstoreOrdersTableRow
            changeOrderStatusHandler={(
              instoreOrder: InstoreOrder,
              status: string
            ) => changeOrderStatusHandler(instoreOrder, status)}
            deleteHandler={() => deleteHandler(instoreOrder)}
            isAdmin={isAdmin}
            isShop={isShop}
            showActions={showActions}
            showDecline={showDecline}
            instoreOrder={instoreOrder}
            enableSelection={enableSelection}
            isSelected={selected.includes(instoreOrder.id)}
            onToggleSelected={() =>
              dispatch(toggleSelectedInstoreOrder(instoreOrder.id))
            }
            key={instoreOrder.id}
          />
        ))}
      </MainTable>
      {clientPaginate && !hide && (
        <PaginationBarClient
          totalAmmount={items.length}
          pageSize={pageSize}
          pageCount={pageCount}
          currentPage={pageIndex + 1}
          prevPageAvailable={pageIndex > 0}
          nextPageAvailable={pageIndex < pageCount - 1}
          firstPageHandler={() => setPageIndex(0)}
          prevPageHandler={() => setPageIndex((p) => Math.max(0, p - 1))}
          nextPageHandler={() =>
            setPageIndex((p) => Math.min(pageCount - 1, p + 1))
          }
          lastPageHandler={() => setPageIndex(pageCount - 1)}
          changePageSizeHandler={(size: number) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          type="orders"
        />
      )}
    </Fragment>
  );
  //#endregion
}
